import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  CheckCircle,
  XCircle,
  Send,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  AlertTriangle,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Filter
} from 'lucide-react';

/**
 * ReviewQueueSection - Manages items from the Happy Path pipeline (generated_items collection)
 * 
 * Status flow: DRAFT -> REVIEW -> APPROVED -> PUBLISHED
 */
const ReviewQueueSection = ({ getAuthHeaders, selectedExam }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ draft: 0, review: 0, approved: 0, published: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('REVIEW'); // Default to REVIEW
  const [expandedItem, setExpandedItem] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [approving, setApproving] = useState({});

  // Fetch items from unified questions collection
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams({
        page_size: '50'
      });
      
      if (selectedExam) {
        params.append('exam_profile_id', selectedExam);
      }
      if (statusFilter && statusFilter !== 'ALL') {
        params.append('status', statusFilter);
      }
      
      // P2: Use unified questions endpoint
      const response = await fetch(`${API}/admin/questions/unified?${params}`, { headers });
      if (!response.ok) {
        // Fallback to legacy endpoint
        const legacyResponse = await fetch(`${API}/generate/items?${params}`, { headers });
        if (!legacyResponse.ok) throw new Error('Failed to fetch items');
        const data = await legacyResponse.json();
        setItems(data.items || []);
        return;
      }
      
      const data = await response.json();
      // Map question_id to item_id for compatibility
      const mappedItems = (data.items || []).map(q => ({
        ...q,
        item_id: q.question_id || q.item_id,
        llm_mode: q.provenance?.llm_mode || q.provenance?.generation_mode || 'unknown'
      }));
      setItems(mappedItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Error loading items');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedExam, statusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const params = selectedExam ? `?exam_profile_id=${selectedExam}` : '';
      const response = await fetch(`${API}/generate/stats${params}`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Count by status from items
        const statusCounts = { draft: 0, review: 0, approved: 0, published: 0, rejected: 0 };
        // Note: The stats endpoint might not have status breakdown, so we'll calculate from items
        setStats(prev => ({ ...prev, total: data.total_items_generated || 0 }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getAuthHeaders, selectedExam]);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [fetchItems, fetchStats]);

  // Approve a single item
  const handleApprove = async (itemId) => {
    setApproving(prev => ({ ...prev, [itemId]: true }));
    try {
      const headers = getAuthHeaders();
      
      // P2: Use unified endpoint for status change
      const response = await fetch(`${API}/admin/questions/unified/${itemId}/status?new_status=APPROVED`, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        // Fallback to legacy endpoint
        const legacyResponse = await fetch(`${API}/generate/items/${itemId}/approve`, {
          method: 'POST',
          headers
        });
        if (!legacyResponse.ok) {
          const error = await legacyResponse.json();
          throw new Error(error.detail?.admin_hint || error.detail || 'Approval failed');
        }
      }
      
      toast.success('Item genehmigt');
      fetchItems();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setApproving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Reject an item
  const handleReject = async (itemId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      const headers = getAuthHeaders();
      
      // P2: Use unified endpoint
      const response = await fetch(`${API}/admin/questions/unified/${itemId}/status?new_status=REJECTED&reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        // Fallback to legacy
        const legacyResponse = await fetch(`${API}/generate/items/${itemId}/reject?reason=${encodeURIComponent(reason)}`, {
          method: 'POST',
          headers
        });
        if (!legacyResponse.ok) throw new Error('Rejection failed');
      }
      
      toast.success('Item rejected');
      fetchItems();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete an item
  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API}/generate/items/${itemId}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      toast.success('Item deleted');
      fetchItems();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Publish all approved items
  const handlePublishApproved = async () => {
    if (!selectedExam) {
      toast.error('Please select an exam first');
      return;
    }
    
    setPublishing(true);
    try {
      const headers = getAuthHeaders();
      
      // P2: Use unified bulk publish endpoint
      const response = await fetch(`${API}/admin/questions/unified/bulk-publish?exam_profile_id=${selectedExam}&limit=100`, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        // Fallback to legacy endpoint
        const legacyResponse = await fetch(`${API}/generate/items/publish-to-questions?exam_profile_id=${selectedExam}&limit=50`, {
          method: 'POST',
          headers
        });
        if (!legacyResponse.ok) throw new Error('Publish failed');
        const data = await legacyResponse.json();
        toast.success(`${data.published_count} items published!`);
        fetchItems();
        return;
      }
      
      const data = await response.json();
      toast.success(`${data.published_count} items published!`);
      fetchItems();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPublishing(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'Draft' },
      'REVIEW': { color: 'bg-blue-100 text-blue-700 border-blue-300', label: 'In Review' },
      'APPROVED': { color: 'bg-green-100 text-green-700 border-green-300', label: 'Approved' },
      'PUBLISHED': { color: 'bg-purple-100 text-purple-700 border-purple-300', label: 'Published' },
      'REJECTED': { color: 'bg-red-100 text-red-700 border-red-300', label: 'Abgelehnt' }
    };
    const config = statusConfig[status] || statusConfig['DRAFT'];
    return <Badge className={`${config.color} border`}>{config.label}</Badge>;
  };

  // Get LLM mode badge
  const getLLMBadge = (llmMode) => {
    if (llmMode === 'live') {
      return <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">LLM</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 border border-amber-300">MOCK</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Sparkles className="w-5 h-5" />
                Happy Path Review Queue
              </CardTitle>
              <CardDescription>
                Items from the generation pipeline
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchItems}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={handlePublishApproved}
                disabled={publishing || !selectedExam}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {publishing ? (
                  <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Publishing...</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" />Publish Approved</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        <span className="text-sm text-gray-500 self-center mr-2">
          <Filter className="w-4 h-4 inline mr-1" />
          Status:
        </span>
        {['ALL', 'DRAFT', 'REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED'].map(status => (
          <Button
            key={status}
            size="sm"
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status ? '' : 'hover:bg-gray-100'}
          >
            {status === 'ALL' ? 'All' : status}
          </Button>
        ))}
      </div>

      {/* Items List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Generated Items ({items.length})
            {selectedExam && <Badge variant="outline">{selectedExam}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Loading items...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No items with status "{statusFilter}" found.</p>
              <p className="text-sm mt-1">
                Generate new questions via the Happy Path Wizard.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {items.map(item => (
                <div
                  key={item.item_id}
                  className={`p-4 border rounded-lg transition-all ${
                    item.llm_mode === 'mock' ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'
                  }`}
                >
                  {/* Header Row */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-blue-600">
                        {item.item_id}
                      </span>
                      {getStatusBadge(item.status)}
                      {getLLMBadge(item.llm_mode)}
                      {item.publish_blocked && (
                        <Badge className="bg-red-100 text-red-700 border border-red-300">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Blocked
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(item.generated_at).toLocaleString('en-US')}
                    </span>
                  </div>

                  {/* Question Preview */}
                  <p className="text-sm mb-3 line-clamp-2">
                    {item.question_text?.substring(0, 200)}
                    {item.question_text?.length > 200 && '...'}
                  </p>

                  {/* Mock Warning */}
                  {item.llm_mode === 'mock' && (
                    <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded text-xs text-amber-800">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      MOCK generated - This question needs to be regenerated with LLM!
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExpandedItem(expandedItem === item.item_id ? null : item.item_id)}
                    >
                      {expandedItem === item.item_id ? (
                        <><EyeOff className="w-4 h-4 mr-1" />Close</>
                      ) : (
                        <><Eye className="w-4 h-4 mr-1" />Details</>
                      )}
                    </Button>

                    {/* Status-specific actions */}
                    {(item.status === 'DRAFT' || item.status === 'REVIEW') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(item.item_id)}
                          disabled={approving[item.item_id] || item.publish_blocked}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {approving[item.item_id] ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          )}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(item.item_id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(item.item_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Expanded Details */}
                  {expandedItem === item.item_id && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      {/* Full Question */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase">Question:</label>
                        <p className="mt-1 p-2 bg-gray-50 rounded text-sm">
                          {item.question_text}
                        </p>
                      </div>

                      {/* All Answer Choices with Explanations */}
                      {item.choices && item.choices.length > 0 ? (
                        <div>
                          <label className="text-xs font-semibold text-gray-600 uppercase">Answer Choices:</label>
                          <div className="mt-1 space-y-2">
                            {item.choices.map((choice, idx) => {
                              const isCorrect = choice.is_correct;
                              return (
                                <div 
                                  key={choice.id || idx} 
                                  className={`p-3 rounded text-sm border ${
                                    isCorrect 
                                      ? 'bg-green-50 border-green-300' 
                                      : 'bg-red-50 border-red-200'
                                  }`}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className={`font-bold ${isCorrect ? 'text-green-700' : 'text-red-600'}`}>
                                      {choice.id})
                                    </span>
                                    <span className={isCorrect ? 'text-green-800' : 'text-gray-800'}>
                                      {choice.text}
                                    </span>
                                    {isCorrect && (
                                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    )}
                                  </div>
                                  {choice.explanation && (
                                    <p className={`mt-2 text-xs ${isCorrect ? 'text-green-700' : 'text-red-600'}`}>
                                      <strong>{isCorrect ? '✓ Why correct:' : '✗ Why wrong:'}</strong> {choice.explanation}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Fallback: Legacy format - Correct Answer */}
                          <div>
                            <label className="text-xs font-semibold text-green-600 uppercase">Correct Answer:</label>
                            <p className="mt-1 p-2 bg-green-50 rounded text-sm">
                              {item.correct_answer}
                            </p>
                            {(item.structured_explanation?.correct_answer_rationale || item.correct_answer_explanation) && (
                              <p className="mt-1 p-2 bg-green-50/50 rounded text-xs text-green-700">
                                <strong>Why correct:</strong> {item.structured_explanation?.correct_answer_rationale || item.correct_answer_explanation}
                              </p>
                            )}
                          </div>

                          {/* Fallback: Legacy format - Distractors */}
                          {item.distractors && item.distractors.length > 0 && (
                            <div>
                              <label className="text-xs font-semibold text-red-600 uppercase">Wrong Answers:</label>
                              <div className="mt-1 space-y-1">
                                {item.distractors.map((d, idx) => {
                                  const distractorKey = String.fromCharCode(66 + idx);
                                  const explanation = 
                                    item.structured_explanation?.why_distractors_wrong?.[distractorKey] ||
                                    item.distractor_explanations?.[idx];
                                  
                                  return (
                                    <div key={`distractor-${distractorKey}-${idx}`} className="p-2 bg-red-50 rounded text-sm">
                                      <span className="font-semibold mr-2">{distractorKey})</span>
                                      {d}
                                      {explanation && (
                                        <p className="text-xs text-red-600 mt-1">
                                          <strong>Why wrong:</strong> {explanation}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Reference */}
                      {item.reference && (
                        <div>
                          <label className="text-xs font-semibold text-blue-600 uppercase">Reference:</label>
                          <p className="mt-1 p-2 bg-blue-50 rounded text-sm">
                            {item.reference}
                          </p>
                        </div>
                      )}

                      {/* Metadata */}
                      <Separator />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Rack:</span>
                          <span className="ml-1 font-mono">{item.rack_id}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Difficulty:</span>
                          <span className="ml-1">{item.difficulty_band}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Model:</span>
                          <span className="ml-1">{item.generation_model}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Mode:</span>
                          <span className="ml-1">{item.generation_mode}</span>
                        </div>
                      </div>

                      {/* Block Reason */}
                      {item.publish_blocked && (
                        <div className="p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700">
                          <AlertTriangle className="w-4 h-4 inline mr-1" />
                          <strong>Blocked:</strong> {item.publish_blocked_reason}
                          {item.risk_flags?.length > 0 && (
                            <div className="mt-1 text-xs">
                              Risk Flags: {item.risk_flags.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewQueueSection;
